/**
 * Canonical React-Query cache keys for PDPL — coerce the assessment id to a
 * Number once so consumers can't accidentally register the same cache under
 * both `['sdaiaResponses', 42]` and `['sdaiaResponses', '42']`. Reading one
 * then invalidating the other was a silent no-op that masked stale data.
 */
export const pdplCacheKeys = {
  sdaiaResponses: (assessmentId) =>
    assessmentId == null
      ? ['sdaiaResponses', null]
      : ['sdaiaResponses', Number(assessmentId)],
  pdplEvidence: (assessmentId) =>
    assessmentId == null
      ? ['pdplEvidence', null]
      : ['pdplEvidence', Number(assessmentId)],
  pdplScoreBreakdown: (assessmentId) =>
    assessmentId == null
      ? ['pdplScoreBreakdown', null]
      : ['pdplScoreBreakdown', Number(assessmentId)],
  pdplArticleEvidence: (assessmentId) =>
    assessmentId == null
      ? ['pdplArticleEvidence', null]
      : ['pdplArticleEvidence', Number(assessmentId)],
  evidenceVersionReviews: (assessmentId) =>
    assessmentId == null
      ? ['evidenceVersionReviews', null]
      : ['evidenceVersionReviews', Number(assessmentId)],
  evidenceSuggestions: (assessmentId, articleRefs, questionId) =>
    [
      'evidenceSuggestions',
      assessmentId == null ? null : Number(assessmentId),
      Array.isArray(articleRefs) ? articleRefs.join('|') : (articleRefs || null),
      questionId == null ? null : Number(questionId),
    ],
  evidenceVersions: (evidenceId) =>
    evidenceId == null
      ? ['evidenceVersions', null]
      : ['evidenceVersions', Number(evidenceId)],
  pdplEvidenceAccessLog: (evidenceId) =>
    evidenceId == null
      ? ['pdplEvidenceAccessLog', null]
      : ['pdplEvidenceAccessLog', Number(evidenceId)],

  // ── Spec 020 Wave B ──────────────────────────────────────────────────────
  pdplCategories: (assessmentId) =>
    assessmentId == null
      ? ['pdplCategories', null]
      : ['pdplCategories', Number(assessmentId)],
  pdplCategoryQuestions: (assessmentId, categoryId) =>
    (assessmentId == null || !categoryId)
      ? ['pdplCategoryQuestions', null, null]
      : ['pdplCategoryQuestions', Number(assessmentId), String(categoryId)],

  // ── Spec 020 Wave C1 ─────────────────────────────────────────────────────
  pdplAttestationText: (categoryId, locale = 'en') =>
    !categoryId
      ? ['pdplAttestationText', null, locale]
      : ['pdplAttestationText', String(categoryId), locale],
  pdplQuestionActivity: (assessmentId, questionExtId) =>
    (assessmentId == null || !questionExtId)
      ? ['pdplQuestionActivity', null, null]
      : ['pdplQuestionActivity', Number(assessmentId), String(questionExtId)],
  pdplAuditLog: (filters = {}) =>
    ['pdplAuditLog', filters],
  pdplApprovalEvents: (filters = {}) =>
    ['pdplApprovalEvents', filters],

  // ── Spec 020 Wave D ──────────────────────────────────────────────────
  pdplSubmissions: (filters = {}) => ['pdplSubmissions', filters],
  pdplSubmission: (submissionId) =>
    submissionId == null
      ? ['pdplSubmission', null]
      : ['pdplSubmission', Number(submissionId)],
  pdplSubmissionVerify: (submissionId) =>
    submissionId == null
      ? ['pdplSubmissionVerify', null]
      : ['pdplSubmissionVerify', Number(submissionId)],

  // ── Spec 020 Assessment Intelligence ─────────────────────────────────
  pdplIntelRuns: (assessmentId) =>
    assessmentId == null
      ? ['pdplIntelRuns', null]
      : ['pdplIntelRuns', Number(assessmentId)],
  pdplIntelRunDetail: (runId) =>
    runId == null
      ? ['pdplIntelRunDetail', null]
      : ['pdplIntelRunDetail', Number(runId)],
  pdplIntelRunStatus: (runId) =>
    runId == null
      ? ['pdplIntelRunStatus', null]
      : ['pdplIntelRunStatus', Number(runId)],
  pdplIntelPreview: (assessmentId) =>
    assessmentId == null
      ? ['pdplIntelPreview', null]
      : ['pdplIntelPreview', Number(assessmentId)],
};
